import { Id } from "@dgs/core";
import { createContext, useContext } from "react";
import {
	CompanionResource,
	CompanionsByType,
	DataFieldValues,
	IConsent,
	RegistrationFormResource,
	RegistrationFormSectionResourceBase,
	RegistrationFormSectionResourceTypeCompanionSection,
	RegistrationFormStaticStepResource,
	RegistrationFormStepResource,
	RegistrationShopSummaryResource,
	RegistrationSummaryNotAvailableResource,
} from "../../shared/types";
import { CompanionType } from "../../shared/types/companionType";
import {
	HotelSectionFormValues,
	HotelStayFormValues,
	HotelSummaryRequest,
	HotelSummaryResource,
	StayConfigurationResource,
} from "../../shared/types/hotel";
import { GuestTravelFormValues } from "../../shared/types/travel";
import { toGuestAttributeChildrenFromSection } from "../registrationFormUtils";

export type ConsentValues = Record<string, boolean | string>;
export type FilledSteps = Record<number, Record<string, any>>;

export type DataFieldErrors = { [key in keyof any]?: string };
export type StepIndex = number | null;

export interface IRegistrationSubmitValues {
	summaryPageConsents: ConsentValues;
	paymentProviderId: Id | null;
	shopSummaryHash: string | null;
}

export interface CurrentCompanion {
	index: number;
	companionType: CompanionType;
	companion: CompanionResource;
}

export type CompanionErrors = Record<number, DataFieldErrors>;

export interface HotelSectionValidationResult {
	count?: string;
}

export interface RegistrationFormContextState {
	errors: Record<string, string>;
	filledSteps: FilledSteps;
	givenConsents: ConsentValues;
	hasSeenLanding: boolean;
	companionsByType: CompanionsByType[];
	saveCompanion: (
		currentCompanion: CurrentCompanion,
		setError: (error: Record<number, string> | null) => void,
		section: RegistrationFormSectionResourceTypeCompanionSection,
	) => void;
	removeCompanion: (
		index: number,
		companionTypeId: number,
		setError: (error: Record<number, string> | null) => void,
		section: RegistrationFormSectionResourceTypeCompanionSection,
	) => void;
	registrationForm: RegistrationFormResource;
	previousStep: (currentStepId?: number) => StepIndex;
	submitStep: (id: number, values: DataFieldValues) => Promise<StepIndex>;
	validateStep: (step: RegistrationFormStepResource, values: DataFieldValues) => Record<string, any>;
	validateStaticStep: (step: RegistrationFormStaticStepResource, values: DataFieldValues) => Record<string, any>;
	submitRegistration: (submitValues: IRegistrationSubmitValues) => Promise<boolean>;
	isShopEnabled: boolean;
	submitRegistrationFormDataForShopSummary: (
		submitValues: IRegistrationSubmitValues,
	) => Promise<RegistrationShopSummaryResource | RegistrationSummaryNotAvailableResource>;
	landingConsents: IConsent[];
	areConsentsLoading: boolean;
	submitLanding: (consents: ConsentValues) => number | null;
	hiddenSteps: number[];
	getHiddenSections: (filledSteps: FilledSteps) => number[];
	getHiddenDataFields: (filledSteps: FilledSteps) => string[];
	isPreview: boolean;
	clearRegistration: () => void;
	declineRegistration: (dataFieldValues: DataFieldValues) => Promise<void>;
	cancelRegistration: (dataFieldValues: DataFieldValues) => Promise<void>;
	nominateReplacement: (dataFieldValues: DataFieldValues) => Promise<void>;
	travels: GuestTravelFormValues[];
	setTravels: (travels: GuestTravelFormValues[]) => void;
	companionFormValidation: (
		stepId: number,
		stepFormValues: Record<number, any>,
		section: RegistrationFormSectionResourceTypeCompanionSection,
		values: Record<string, any>,
	) => Record<string, any>;
	validateCompanions: (
		stepId: number,
		stepFormValues: Record<number, any>,
		section: RegistrationFormSectionResourceTypeCompanionSection,
	) => CompanionErrors;
	getStayConfigurationValidation: (
		stayConfiguration: StayConfigurationResource,
	) => (values: HotelSectionFormValues | null) => HotelSectionValidationResult | undefined;
	getHotelStayValidation: (
		stayConfiguration: StayConfigurationResource,
		otherStays: HotelStayFormValues[],
	) => (values: HotelStayFormValues | null) => undefined | string;
	getHotelSummary: (
		sectionId: number,
		request: HotelSummaryRequest,
	) => {
		data: HotelSummaryResource[] | undefined;
		isLoading: boolean;
	};
	setHasSeenLanding: (hasSeenLanding: boolean) => void;
}

export const RegistrationFormContext = createContext<RegistrationFormContextState | undefined>(undefined);

export const useRegistrationForm = () => {
	const context = useContext(RegistrationFormContext);

	if (context === undefined) {
		throw new Error(`Context undefined. Are you missing the RegistrationFormProvider?`);
	}

	return context;
};

export const useIsAvailableStep = (stepId: number) => {
	const { hiddenSteps, registrationForm } = useRegistrationForm();
	const step = registrationForm.steps.find((x) => x.id === stepId);

	if (!step) {
		return null;
	}
	return { isAvailable: !hiddenSteps.includes(stepId), step };
};

export const useIsAvailableSection = (dataFieldValues: Record<number, any>, stepId: number, sectionId: number) => {
	const { getHiddenSections, filledSteps } = useRegistrationForm();
	return !getHiddenSections({
		...filledSteps,
		[stepId]: dataFieldValues,
	}).includes(sectionId);
};

export const useHiddenDataFieldIdsInSection = (
	stepValues: Record<number, any>,
	stepId: number,
	section: RegistrationFormSectionResourceBase,
) => {
	const { getHiddenDataFields, filledSteps } = useRegistrationForm();
	const guestAttributes = toGuestAttributeChildrenFromSection(section);
	const hiddenDataFields = getHiddenDataFields({ ...filledSteps, [stepId]: stepValues });

	return guestAttributes.flatMap(({ dataField }) => {
		if (hiddenDataFields.includes(`${section.id}-${dataField.id}`)) {
			return [dataField.id];
		} else {
			return [];
		}
	});
};
